import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Card, CardHeader, CardBody, Table, Button, Badge, Modal, ModalHeader, ModalBody,
  ModalFooter, FormGroup, Label, Input, Spinner,
} from 'reactstrap';
import Swal from 'sweetalert2';
import {
  updateResourceFile, replaceResourceFile, deleteResourceFile, getResourceFileUrl,
} from 'redux/ResourceFiles/ResourceFilesAction';
import { formatFileSize } from 'util/resourceFiles';

const ResourceFileTable = ({
  files, loading, updateResourceFile, replaceResourceFile, deleteResourceFile,
}) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const replaceRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);

  const openEdit = (file) => {
    setEditing({
      fileId: file.fileId,
      title: file.title || '',
      description: file.description || '',
      active: file.active !== false,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    const { fileId, ...updates } = editing;
    const result = await updateResourceFile(fileId, updates);
    setSaving(false);

    if (result.success) {
      setEditing(null);
    } else {
      Swal.fire({ title: 'Save failed', text: result.error, icon: 'error' });
    }
  };

  const handleCopyLink = async (file) => {
    try {
      const url = await getResourceFileUrl(file.storagePath);
      await navigator.clipboard.writeText(url);
      Swal.fire({ title: 'Link copied', icon: 'success', timer: 1800, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Could not copy link', text: err.message, icon: 'error' });
    }
  };

  const handleDownload = async (file) => {
    try {
      const url = await getResourceFileUrl(file.storagePath);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      Swal.fire({ title: 'Could not open file', text: err.message, icon: 'error' });
    }
  };

  const handleReplaceClick = (fileId) => {
    setReplacingId(fileId);
    if (replaceRef.current) {
      replaceRef.current.value = '';
      replaceRef.current.click();
    }
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !replacingId) return;

    const result = await replaceResourceFile(replacingId, file);
    setReplacingId(null);

    if (result.success) {
      Swal.fire({ title: 'File replaced', icon: 'success', timer: 2000 });
    } else {
      Swal.fire({ title: 'Replace failed', text: result.error, icon: 'error' });
    }
  };

  const handleDelete = async (file) => {
    const confirm = await Swal.fire({
      title: 'Delete this file?',
      text: `"${file.title}" will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      confirmButtonText: 'Delete',
    });
    if (!confirm.isConfirmed) return;

    const result = await deleteResourceFile(file.fileId, file.storagePath);
    if (!result.success) {
      Swal.fire({ title: 'Delete failed', text: result.error, icon: 'error' });
    }
  };

  return (
    <Card className="shadow mt-4 mb-5">
      <CardHeader className="border-0 d-flex justify-content-between align-items-center">
        <h3 className="mb-0">Files</h3>
        {loading && <Spinner size="sm" color="primary" />}
      </CardHeader>
      <CardBody className="p-0">
        <input
          ref={replaceRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleReplaceSelected}
        />

        {(!files || files.length === 0) && !loading ? (
          <div className="text-center py-5 text-muted">
            No files uploaded for this specialty yet.
          </div>
        ) : (
          <Table className="align-items-center table-flush" responsive>
            <thead className="thead-light">
              <tr>
                <th scope="col">Title</th>
                <th scope="col">File</th>
                <th scope="col">Description</th>
                <th scope="col">Size</th>
                <th scope="col">Downloads</th>
                <th scope="col">Uploaded</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {(files || []).map((file) => (
                <tr key={file.fileId} className={file.active === false ? 'text-muted' : ''}>
                  <td>
                    {file.title}
                    {file.active === false && (
                      <Badge color="secondary" className="ml-2">Inactive</Badge>
                    )}
                  </td>
                  <td><small>{file.fileName}</small></td>
                  <td><small>{file.description || '—'}</small></td>
                  <td><small>{formatFileSize(file.sizeBytes)}</small></td>
                  <td><small>{file.downloadCount || 0}</small></td>
                  <td>
                    <small>
                      {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : '—'}
                    </small>
                  </td>
                  <td className="text-right">
                    <Button size="sm" color="secondary" onClick={() => handleDownload(file)} title="Open">
                      <i className="fas fa-download" />
                    </Button>
                    <Button size="sm" color="secondary" onClick={() => handleCopyLink(file)} title="Copy link">
                      <i className="fas fa-link" />
                    </Button>
                    <Button size="sm" color="secondary" onClick={() => handleReplaceClick(file.fileId)} title="Replace file">
                      <i className="fas fa-sync-alt" />
                    </Button>
                    <Button size="sm" color="primary" onClick={() => openEdit(file)} title="Edit">
                      <i className="fas fa-edit" />
                    </Button>
                    <Button size="sm" color="danger" onClick={() => handleDelete(file)} title="Delete">
                      <i className="fas fa-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>

      <Modal isOpen={!!editing} toggle={() => setEditing(null)}>
        <ModalHeader toggle={() => setEditing(null)}>Edit File</ModalHeader>
        {editing && (
          <ModalBody>
            <FormGroup>
              <Label>Title</Label>
              <Input
                type="text"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                rows="3"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />{' '}
                Active
              </Label>
            </FormGroup>
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" onClick={saveEdit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  files: state.resourceFiles.files,
  loading: state.resourceFiles.loading,
});

export default connect(mapStateToProps, {
  updateResourceFile, replaceResourceFile, deleteResourceFile,
})(ResourceFileTable);
